import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { InfoGridProps } from "@/v2-backoffice-client/components/infoGrid"
import InfoGrid, {
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import { FENERUM_ACCOUNT_BASE_URL } from "@/v2-backoffice-client/constants"
import {
  ACKNOWLEDGED_STATUS,
  getLinkStatus,
  LINKED_STATUS,
  UNMATCHED_STATUS,
} from "@/v2-backoffice-client/license/account/license.account.utils"
import type { LicenseAccountFragment } from "@/v2-backoffice-client/license/account/__types__/license.account.fragment"

const FIELDS = createInfoGridFields<LicenseAccountFragment>({
  fields: {
    // The link is the account's whole story: matched accounts feed the
    // customer's billing tab and license import; unmatched ones are orphans
    // until the org-number data is fixed (or the state is acknowledged as
    // intentional from the Customers tab).
    LinkStatus: {
      label: "Customer Link",
      accessor: (data) => getLinkStatus(data),
      renderValue({ data }) {
        const status = getLinkStatus(data)
        const variant =
          status === LINKED_STATUS
            ? "success-1"
            : status === UNMATCHED_STATUS
              ? "destructive"
              : status === ACKNOWLEDGED_STATUS
                ? "warning"
                : "muted"
        return <DotText variant={variant}>{status}</DotText>
      },
    },
    Customer: {
      label: "Customer",
      accessor: (data) => data.CustomerName ?? null,
      to: ({ data }) =>
        data.ExternalCustomerId
          ? `>>?v=customer:${data.ExternalCustomerId}`
          : undefined,
    },
    UnmatchedReason: {
      label: "Unmatched Reason",
      accessor: (data) => data.UnmatchedReason ?? null,
      valueClassName: "truncate",
    },
    // The acknowledgment: who reviewed the unmatched state and when — the
    // note itself lives on the Customers tab (and the board's tooltip).
    ReviewedBy: {
      label: "Acknowledged",
      accessor: (data) =>
        data.ReviewedAt
          ? `${data.ReviewedByUserName ?? "Yes"} — ${formatDateString(data.ReviewedAt)}`
          : null,
      valueClassName: "truncate",
    },
    LinkedBy: {
      label: "Linked By",
      accessor: (data) =>
        data.CustomerId != null
          ? (data.LinkedByUserName ?? "Hourly mirror (org number)")
          : null,
      defaultHidden: true,
    },
    LegalVatNumber: {
      label: "Org Number",
      accessor: (data) => data.LegalVatNumber ?? "Missing",
      renderValue({ data }) {
        if (!data.LegalVatNumber) {
          return <DotText variant="destructive">Missing</DotText>
        }
        return data.LegalVatNumber
      },
    },
    SellingEntity: {
      label: "Selling Entity",
      accessor: "SellingEntity",
    },
    IsEanInvoicing: {
      label: "EAN Invoicing",
      accessor: (data) => (data.IsEanInvoicing ? "Yes" : "No"),
      defaultHidden: true,
    },
    LanguageCode: {
      label: "Language",
      accessor: "LanguageCode",
      defaultHidden: true,
    },
    LegalCountry: {
      label: "Country",
      accessor: "LegalCountry",
      defaultHidden: true,
    },
    ErpId: {
      label: "ERP Id",
      accessor: "ErpId",
      defaultHidden: true,
    },
    ExternalAccountCode: {
      label: "Fenerum Code",
      accessor: "ExternalAccountCode",
      defaultHidden: true,
    },
    ExternalAccountUuid: {
      label: "Fenerum Id",
      accessor: "ExternalAccountUuid",
      to: ({ data }) =>
        `${FENERUM_ACCOUNT_BASE_URL}/${data.ExternalAccountUuid}/`,
      valueClassName: "truncate",
      defaultHidden: true,
    },
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
  },
})

export type LicenseAccountViewInfoProps = Omit<
  InfoGridProps<LicenseAccountFragment>,
  "data" | "fields"
> & {
  account: LicenseAccountFragment | null | undefined
}

function LicenseAccountViewInfo(props: LicenseAccountViewInfoProps) {
  const { account, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="license-account-info"
      className={cn("license-account-view-info", className)}
      data={account}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default LicenseAccountViewInfo
